import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, SendOutlined as DesignSend } from "@mui/icons-material";
// Ch83 · send a text from the work list, in one click.
//
// The Admin card settled it: 10 outbound texts in this system's entire history,
// one reply ever. The pipe works — it has always worked. The loop was the
// problem: see the lead, open the modal, read the phone, pick up your OWN
// phone, text, come back, press "Log Text". Seven steps across two devices, and
// the button at the end records the past instead of doing the thing.
//
// So: a draft already written, one Send, and the record updates itself.
// last_contacted_at becomes TRUE rather than dependent on somebody remembering
// to log it — which is also what makes #538's `contacted` count mean anything.
//
// It never opens on a person the server would refuse: sendability() decides
// that before the button is drawn, and the row disables itself with the reason.

import React from "react";
import { smsLengthWarning, smsSegmentInfo } from "../../lib/smsSegments";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from "@mui/material";
import { sendability, sendOutcome, draftOpener, type Sender } from "../../lib/leadsSend";
import { loadSendTemplates, openingDraft, resolveForRow, type SendTemplate } from "../../lib/leadsTemplates";
import { refusalMessage } from "../../lib/api";
import { recordSentText } from "../../lib/recordSentText";
import type { WorkRow } from "../../lib/leadsWork";
import { sendClientSms } from "../../services/clientSmsService";
import { sendLeadSms } from "../../services/smsService";

/** Twilio bills per segment; 160 GSM-7 characters is the first one. */
const SEGMENT = 160;

export interface SendFromRowDialogProps {
  row: WorkRow | null;
  senderName?: string | null;
  /** PKT-0666 · who is sending; the composer refuses what the route would. */
  sender: Sender;
  onClose: () => void;
  /** Called after a send lands, so the page can re-read who has been contacted. */
  onSent: () => void;
}

export default function SendFromRowDialog({ row, senderName, sender, onClose, onSent }: SendFromRowDialogProps) {
  const [body, setBody] = React.useState("");
  const [sending, setSending] = React.useState(false);
  // A ref, not the state, because state is what loses the double-click race.
  const sendingRef = React.useRef(false);
  const [templates, setTemplates] = React.useState<SendTemplate[]>([]);
  const [templateId, setTemplateId] = React.useState<string | null>(null);
  const [unresolved, setUnresolved] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  // A fresh draft per person, not a sticky buffer: sending Ava's message to Ben
  // is the kind of mistake a shared textbox makes for you.
  React.useEffect(() => {
    setError(null);
    setSending(false);
    sendingRef.current = false;
    if (!row) {
      setBody("");
      setTemplates([]);
      setTemplateId(null);
      setUnresolved([]);
      return;
    }
    // Seed SYNCHRONOUSLY. The template shelf is a network read, and a composer
    // that opens blank with Send greyed out — even for 200ms — is a composer
    // the operator learns to distrust. #540's generated opener fills the gap.
    const seeded = draftOpener(row, senderName);
    setBody(seeded);
    setTemplateId(null);
    setUnresolved([]);
    // The shelf is per-person: which templates are offered FIRST depends on
    // whether this is an opener or a follow-up, and that is a property of the
    // row. Guarded against a late response landing after the operator has
    // moved on — otherwise Ava's template overwrites the draft Ben is typing.
    let current = true;
    loadSendTemplates(row).then(({ templates: loaded }) => {
      if (!current) return;
      const opening = openingDraft(loaded, row, senderName);
      setTemplates(loaded);
      setTemplateId(opening.templateId);
      setUnresolved(opening.unresolved);
      // Only upgrade a draft the operator has NOT touched. A late read that
      // overwrites half-typed words is worse than showing the seeded opener.
      setBody((prev) => (prev === seeded ? opening.body : prev));
    });
    return () => {
      current = false;
    };
  }, [row, senderName]);

  if (!row) return null;
  const can = sendability(row, sender);
  const trimmed = body.trim();
  const segments = Math.max(1, Math.ceil(trimmed.length / SEGMENT));
  const lengthWarning = smsLengthWarning(smsSegmentInfo(trimmed));

  const send = async () => {
    // `sending` is in here, and that is the whole dedupe. The button's disabled
    // prop is NOT enough on its own: setSending(true) schedules a re-render, and
    // two clicks landing before React commits both pass a guard that only reads
    // props. Found in review of #540 — a double-click sent the text twice, and
    // the person on the other end got it twice. Reading the ref makes the second
    // call a no-op in the same tick.
    if (sendingRef.current) return;
    if (!can.canSend || !can.id || !trimmed) return;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      const response = can.route === "client"
        ? await sendClientSms(can.id, trimmed)
        : await sendLeadSms(can.id, { body: trimmed });

      // HTTP 200 is not delivery. Both routes answer 200 after a timeout, an
      // HTTP error, or scaffold mode, having persisted the attempt — so the
      // BODY is the only thing that says whether a carrier was ever reached.
      // Closing here without reading it is how an operator comes to believe
      // they texted someone they did not.
      const outcome = sendOutcome(can.route, response);
      if (!outcome.left) {
        setError(outcome.why);
        setSending(false);
        sendingRef.current = false;
        return;
      }
      // The text left. Now make the RECORD true, or the work list will keep
      // telling the operator to text someone they just texted — the old "Log
      // Text" button did this and my first version of this dialog dropped it.
      // Awaited so the re-read below sees the bumped column; best effort, so a
      // bookkeeping failure never turns a delivered text into an error.
      if (can.route === "client") await recordSentText(can.id, row.person.name);
      onSent();
      onClose();
    } catch (e) {
      // The server's own words. It refuses for reasons this UI deliberately
      // does not duplicate (consent state, guardrails), and paraphrasing them
      // would hide which one fired.
      //
      // refusalMessage FIRST, and not only because smsService now unwraps its
      // own errors: clientSmsService does no unwrapping at all, so a client
      // consent refusal — a 409 carrying {reason, message, guards_passed}, the
      // likeliest refusal on that route — arrives here as a raw ApiError whose
      // message is the string "API error: 409". Reading the field is what makes
      // this alert worth showing.
      setError(refusalMessage(e) ?? (e instanceof Error ? e.message : String(e)));
      setSending(false);
      sendingRef.current = false;
    }
  };

  return (
    <Dialog open onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5, pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onClose} disabled={sending}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
        <Typography variant="subtitle1" fontWeight={800}>Text {row.person.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.person.kind === "client" ? "Sends on their client thread" : "Sends to the lead"} · recorded automatically
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!can.canSend ? (
          <Alert severity="warning" data-testid="send-blocked">
            {can.reason}. {can.remedy}.
          </Alert>
        ) : (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {templates.length > 0 && (
              <TextField
                select
                size="small"
                label="Template"
                // Native: a locator on a phone gets the OS picker, and the
                // control is a real <select> rather than a portalled menu.
                SelectProps={{ native: true }}
                value={templateId ?? ""}
                onChange={(e) => {
                  const picked = templates.find((t) => t.id === e.target.value);
                  if (!picked || !row) return;
                  const next = resolveForRow(picked, row, senderName);
                  setTemplateId(picked.id);
                  setBody(next.body);
                  setUnresolved(next.unresolved);
                }}
                inputProps={{ "data-testid": "send-template-select" }}
                disabled={sending}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.source === "saved" ? " · yours" : ""}
                  </option>
                ))}
              </TextField>
            )}
            {unresolved.length > 0 && (
              // A missing placeholder interpolates to "" — "Hi ," goes out to a
              // real person unless somebody notices. Not blocking: the text is
              // editable and the operator may have already fixed it.
              <Alert severity="warning" data-testid="send-unresolved">
                Nothing to fill in for {unresolved.join(", ")} — check the message before sending.
              </Alert>
            )}
            <TextField
              multiline
              minRows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              inputProps={{ "data-testid": "send-body", "aria-label": "Message" }}
              disabled={sending}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption" color={lengthWarning ? "warning.main" : "text.secondary"} data-testid="send-count" data-warning={lengthWarning}>
                {trimmed.length} characters · {segments} segment{segments === 1 ? "" : "s"}
                {lengthWarning ? " · Length warning: a link is usually the cause" : ""}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                The draft is a starting point — edit it.
              </Typography>
            </Box>
            {error && (
              <Alert severity="error" data-testid="send-error">
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Typography role="status" aria-live="polite" variant="caption" color="text.secondary">
          {sending ? "Sending…" : ""}
        </Typography>
        <DesignTooltip title={sending ? "Sending…" : "Send text"}>
          <span>
            <DesignIconButton
              aria-label={sending ? "Sending…" : "Send text"}
              onClick={send}
              disabled={!can.canSend || sending || !trimmed}
              data-testid="send-confirm"
              color="primary"
              sx={{ width: 40, height: 40 }}
            >
              {sending
                ? <CircularProgress size={20} color="inherit" aria-label="Sending…" />
                : <DesignSend sx={{ fontSize: 20 }} />}
            </DesignIconButton>
          </span>
        </DesignTooltip>
      </DialogActions>
    </Dialog>
  );
}
